/**
 * Consent Required Page
 *
 * Displayed when user hasn't accepted required legal documents (terms, privacy).
 * User must accept all required documents before accessing protected content.
 */

import { useSaaSConfig } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  cn,
  Label,
  ModalShell,
} from '@xhubio-saas/core-frontend/ui-components'
import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { type MissingConsent, useConsentClient } from '../../api/consentClient'
import { AuthAlert, CONTROL_TRANSITION, FIELD_RING } from './authUi'

interface ConsentRequiredPageProps {
  /** Documents that need acceptance */
  missingConsents: MissingConsent[]
  /** Callback when consent is successfully submitted */
  onConsentAccepted?: () => void
}

/**
 * Page shown when user must accept required consents before continuing
 */
export function ConsentRequiredPage({
  missingConsents,
  onConsentAccepted,
}: ConsentRequiredPageProps) {
  const { t } = useTranslation()
  const config = useSaaSConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const consentClient = useConsentClient()

  const [acceptedDocs, setAcceptedDocs] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The submit button is never validation-disabled (UI rule §9). A click without
  // every box ticked flips this on to surface an inline hint and highlight the
  // still-unchecked consents. It reads as cleared once all boxes are ticked.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const handleCheckboxChange = useCallback((docType: string, checked: boolean) => {
    setAcceptedDocs((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(docType)
      } else {
        newSet.delete(docType)
      }
      return newSet
    })
  }, [])

  const allAccepted = missingConsents.every((doc) => acceptedDocs.has(doc.type))

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return
    if (!allAccepted) {
      // Button stays active (§9): surface the missing consent inline instead of
      // a dead button.
      setAttemptedSubmit(true)
      return
    }

    setAttemptedSubmit(false)
    setIsSubmitting(true)
    setError(null)

    try {
      // Extract just the type strings for submission
      const documentTypes = missingConsents.map((doc) => doc.type)
      // Nothing missing (page opened directly, or consent given meanwhile):
      // there is nothing to record. core-backend >= 1.107.1 rejects an empty
      // list (`documentTypes` `.min(1)`), so send nothing and continue.
      if (documentTypes.length > 0) {
        await consentClient.submitMultiple(documentTypes)
      }

      // Notify parent component
      if (onConsentAccepted) {
        onConsentAccepted()
      }

      // Navigate to intended destination or dashboard
      const from = (location.state as { from?: Location })?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (err) {
      setError(t('consent.error', 'Failed to save your consent. Please try again.'))
      console.error('Failed to submit consent:', err)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    allAccepted,
    isSubmitting,
    consentClient,
    missingConsents,
    onConsentAccepted,
    location.state,
    navigate,
    t,
  ])

  const getDocumentLabel = (docType: string): string => {
    switch (docType) {
      case 'terms':
        return t('consent.acceptTerms', 'I accept the Terms of Service')
      case 'privacy':
        return t('consent.acceptPrivacy', 'I accept the Privacy Policy')
      default:
        return t('consent.acceptDocument', { type: docType })
    }
  }

  const getDocumentLink = (docType: string): string => {
    switch (docType) {
      case 'terms':
        return '/terms'
      case 'privacy':
        return '/privacy'
      default:
        return `/${docType}`
    }
  }

  const getDocumentTitle = (docType: string): string => {
    switch (docType) {
      case 'terms':
        return t('legal.termsTitle', 'Terms of Service')
      case 'privacy':
        return t('legal.privacyTitle', 'Privacy Policy')
      default:
        return docType
    }
  }

  return (
    // 🔴 A wall, not a page: it blocks the app until the user decides — and the
    // shell is now `ModalShell` (core-frontend/ui-components) instead of a
    // hand-rolled one. The defect it fixes: this gate has EXACTLY ONE button and
    // no way out — no cancel, no ✕, no escape. Once the list of legal documents
    // (`missingConsents.map`) outgrew the screen height, „Accept and Continue"
    // dropped below the fold and the app became unusable. `ModalShell` carries
    // the three bands: header and action bar stay put (`shrink-0`), only the
    // body gives way (`min-h-0 overflow-y-auto`) — plan CORE 09-05/02, step 3
    // (rank A).
    //
    // Unchanged: `role`/`aria-*` travel through to the panel, the backdrop still
    // covers the whole viewport (`bg-background/80 backdrop-blur-sm`), and there
    // is deliberately NO `onClose` — without it `ModalShell` listens neither for
    // escape nor for the backdrop click. A route gate must not be dismissable;
    // consent is the only way forward.
    <ModalShell
      open
      size="md"
      testIdPrefix="consent-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-dialog-title"
      backdropClassName="bg-background/80 backdrop-blur-sm"
      // The former `Card` chrome stays: same radius, same hairline, same
      // surface. Only the padding now sits per band (`px-6`) — as one block at
      // the panel the bottom padding would scroll away with the content.
      className="rounded-xl border-[var(--border-subtle)] bg-card text-card-foreground"
      headerClassName="pb-6"
      header={
        <CardHeader className="gap-2">
          <CardTitle id="consent-dialog-title" className="font-heading text-2xl tracking-[-0.04em]">
            {t('consent.title', 'Accept Our Policies')}
          </CardTitle>
          <CardDescription>
            {/* appName must be passed explicitly: the locale key interpolates
                {{appName}}, and only the English default has it baked in. */}
            {t('consent.description', {
              appName: config.name,
              defaultValue: `To continue using ${config.name}, please review and accept our updated policies.`,
            })}
          </CardDescription>
        </CardHeader>
      }
      bodyClassName="flex flex-col gap-6"
      footerClassName="mt-6 flex-col items-stretch gap-3"
      footer={
        // Inline hint uses the same AuthAlert as the error banner; primary
        // action right-aligned — the project's UI_GUIDELINES rule.
        <>
          {attemptedSubmit && !allAccepted && (
            <AuthAlert tone="error">
              {t('consent.selectAllRequired', 'Please accept all policies to continue.')}
            </AuthAlert>
          )}
          <div className="flex justify-end">
            {/* Never validation-disabled (§9): a click without every box ticked
                shows the inline hint above instead of a dead button. */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className={cn(
                'gap-2',
                'transition-[background-color,transform] duration-[var(--dur-micro)] ease-[var(--ease-out)]',
                'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isSubmitting && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              {isSubmitting
                ? t('consent.submitting', 'Saving…')
                : t('consent.submit', 'Accept and Continue')}
            </Button>
          </div>
        </>
      }
    >
      {error && <AuthAlert tone="error">{error}</AuthAlert>}

      {/* Each document is one decision. The hairline between them keeps two
          consents from reading as one checkbox group. */}
      <div className="flex flex-col divide-y">
        {missingConsents.map((doc) => (
          <div key={doc.type} className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
            <Checkbox
              id={`consent-${doc.type}`}
              checked={acceptedDocs.has(doc.type)}
              onCheckedChange={(checked: boolean) =>
                handleCheckboxChange(doc.type, checked === true)
              }
              disabled={isSubmitting}
              aria-invalid={attemptedSubmit && !acceptedDocs.has(doc.type)}
              className={cn(
                'mt-0.5',
                FIELD_RING,
                attemptedSubmit &&
                  !acceptedDocs.has(doc.type) &&
                  'border-destructive ring-1 ring-destructive'
              )}
            />
            <div className="flex flex-col gap-1">
              <Label htmlFor={`consent-${doc.type}`} className="cursor-pointer leading-snug">
                {getDocumentLabel(doc.type)}
              </Label>
              <p className="text-sm text-muted-foreground">
                <Link
                  to={getDocumentLink(doc.type)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'rounded-sm text-primary underline-offset-4 hover:underline',
                    CONTROL_TRANSITION,
                    FIELD_RING
                  )}
                >
                  {t('consent.readDocument', 'Read {{title}}', {
                    title: doc.title || getDocumentTitle(doc.type),
                  })}
                </Link>
                {doc.version && (
                  // Version is data — mono, so a changed number is legible.
                  <span className="ml-2 font-mono text-xs">
                    {t('consent.version', 'Version')} {doc.version}
                  </span>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  )
}
